/**
 * SiteMeta — 현장 1곳에 붙는 우리 쪽 메타데이터 (표시명 + 위치 + 시간 보정).
 *
 * Firestore: `site_meta/{scode}_{hcode}`
 *   doc ID = ERP 현장 마스터(`sales_sites`)와 **같은 키**다. 거래처코드_현장코드.
 *   출하(yejung.scode/hcode)와 영업(d11_code/d16_code)이 공유하는 마스터 코드라,
 *   이 한 벌로 출하·판매·견적·시뮬레이션이 같은 사전을 본다.
 *
 * 옛 구조와의 차이 — 왜 코드 키인가:
 *   site_aliases / site_locations 는 doc ID 가 **현장명 문자열**이었다. 출하 DB 와 영업 DB 가
 *   같은 현장을 조금씩 다르게 적어두면(실측 일치율 64%) 사전이 두 벌로 갈린다. 코드는
 *   두 시스템이 공유하므로 그 문제가 없다.
 *
 * ERP 는 주소·거리·시간 칸을 비워둔 채 쓴다(sales_sites 4,861건 전부 공란, 2026-09 실측).
 * 그래서 현장 위치의 정본은 ERP 가 아니라 여기다.
 */
import type { Coord } from "@/services/tmap";

/** 표시명 출처 — 규칙 결과는 저장하지 않는다(순수 함수라 매번 계산이 더 정확) */
export type SiteDisplaySource = "manual" | "llm";

/**
 * 주소 출처.
 *   manual  — 사람이 직접 입력·확인
 *   memo    — 출하 비고(yejung.remk)에서 추출  ← 사람이 승인한 것만 저장한다
 *   geocode — 지오코딩이 실제로 적중한 주소를 캐시
 */
export type SiteAddressSource = "manual" | "memo" | "geocode";

export interface SiteMeta {
  /** doc ID = `${scode}_${hcode}` */
  id: string;
  /** 거래처 코드 (yejung.scode = d11_code) */
  scode: string;
  /** 현장 코드 (yejung.hcode = d16_code) */
  hcode: string;

  // ── 표시명 (옛 site_aliases) ──────────────────────────────────────
  /** 사람이 확정한 표시명. 없으면 화면은 normalizeSiteName 규칙 결과를 쓴다 */
  display?: string;
  display_source?: SiteDisplaySource;
  /** ERP 원본 현장명 — 마지막으로 관측한 값. 목록·검색 표시용(정본은 ERP) */
  raw_name?: string;
  /** 이관·추적용 옛 현장명 문자열들 (문자열 키 시절 별칭에서 옮겨온 흔적) */
  legacy_names?: string[];

  // ── 위치 (옛 site_locations) ──────────────────────────────────────
  /** 수동 확정 좌표 (지도 클릭 / 주소 검색). 지오코딩 자동 성공분은 저장 안 함 */
  coord?: Coord | null;
  /** 주소 — ERP 에 없으므로 우리가 채운다 */
  address?: string;
  address_source?: SiteAddressSource;

  // ── 시간 보정 (옛 site_locations) ─────────────────────────────────
  /** 편도 이동시간 보정(분) — 미설정이면 경로 계산값 */
  travel_min_override?: number | null;
  /** 현장 체류(타설·대기) 시간 보정(분) — 미설정이면 전역 기본값 */
  onsite_min?: number | null;

  edited_by?: string;
  edited_at?: string;
}

/** `${scode}_${hcode}` — 둘 중 하나라도 비면 null (코드 없는 현장은 규칙 표시명으로 폴백) */
export function siteMetaKey(scode?: string | null, hcode?: string | null): string | null {
  const s = String(scode ?? "").trim();
  const h = String(hcode ?? "").trim();
  return s && h ? `${s}_${h}` : null;
}

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v : undefined;
}

function isoDate(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  const t = v as { toDate?: () => Date } | null | undefined;
  return t && typeof t.toDate === "function" ? t.toDate().toISOString() : undefined;
}

export function coerceSiteMeta(id: string, data: Record<string, unknown>): SiteMeta {
  const c = data.coord as { lat?: unknown; lng?: unknown } | null | undefined;
  const lat = Number(c?.lat);
  const lng = Number(c?.lng);
  const coord =
    Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
      ? { lat, lng }
      : null;

  const src = data.display_source;
  const [scode = "", hcode = ""] = id.split("_");

  return {
    id,
    scode: str(data.scode) ?? scode,
    hcode: str(data.hcode) ?? hcode,
    display: str(data.display),
    raw_name: str(data.raw_name),
    // ⚠ union 에 값을 추가하면 이 화이트리스트도 함께 (silent fallback 방지)
    display_source: src === "manual" || src === "llm" ? src : undefined,
    legacy_names: Array.isArray(data.legacy_names)
      ? data.legacy_names.filter((v): v is string => typeof v === "string")
      : undefined,
    coord,
    address: str(data.address),
    // ⚠ union 에 값을 추가하면 이 화이트리스트도 함께
    address_source:
      data.address_source === "manual" || data.address_source === "memo" || data.address_source === "geocode"
        ? data.address_source
        : undefined,
    travel_min_override: num(data.travel_min_override),
    onsite_min: num(data.onsite_min),
    edited_by: str(data.edited_by),
    edited_at: isoDate(data.edited_at),
  };
}
